import _mergeWith from 'lodash/mergeWith'
import _isObject from 'lodash/isObject'
import _isNull from 'lodash/isNull'
import _has from 'lodash/has'

/**
 * Merges two objects while preserving unique properties.
 * If a property exists in both objects, the value from the source object is used if override is true,
 * or if the target value is null or an empty string, or if the property does not exist in the target object.
 *
 * @param {Record<string, unknown>} targetObject - The target object to merge into.
 * @param {Record<string, unknown>} sourceObject - The source object to merge from.
 * @param {boolean} override - Whether to override existing properties in the target object with values from the source object.
 * @returns {Record<string, unknown>} The merged object.
 */
export function mergeObjectsWithUniqueProps(
  targetObject: Record<string, unknown>,
  sourceObject: Record<string, unknown>,
  override = false
): Record<string, unknown> {
  return _mergeWith(
    { ...targetObject },
    sourceObject,
    (targetValue, sourceValue, key) => {
      if (_isObject(targetValue) && _isObject(sourceValue)) {
        return mergeObjectsWithUniqueProps(
          targetValue as Record<string, unknown>,
          sourceValue as Record<string, unknown>,
          override
        )
      }

      return override ||
        _isNull(targetValue) ||
        (typeof targetValue === 'string' && targetValue === '') ||
        !_has(targetObject, key)
        ? sourceValue
        : targetValue
    }
  )
}
